/**
 * KitStart Navigation Engine
 * 
 * Gestisce la navigazione dinamica tra i servizi del KitStart
 * basata sui servizi selezionati dall'utente.
 * 
 * FLUSSO DI NAVIGAZIONE CORRETTO:
 * 1. Selezione servizi (/kitstart)
 * 2. Compilazione servizi:
 *    - MICROPAYMENTS -> /kitstart/store
 *    - PAYMAIL -> /kitstart/pbl  
 *    - BACKUP_POS/ISP_POS -> /kitstart/softpos -> /kitstart/contact (obbligatorio)
 * 3. Consensi (/kitstart/consents) - SEMPRE mostrati TRANNE per SOLO ISP_POS
 * 4. Riepilogo e OTP (/kitstart/summary -> /kitstart/otp)
 * 
 * NAVIGAZIONE INDIETRO:
 * - Da riepilogo: vai sempre a consensi
 * - Da consensi: vai a contact (se SoftPOS presente) o ultimo servizio
 * - Da contact: vai sempre a softpos
 * - Da servizi: vai al servizio precedente o selezione
 * 
 * @version 1.1.0
 * @author Marco Alessandroni  
 * @since 13-Oct-2025
 */

import { nullSafe } from "Utility/globalMethods"

// Mapping dei servizi alle loro rotte
export const SERVICE_ROUTES = {
  'MICROPAYMENTS': '/kitstart/store',
  'PAYMAIL': '/kitstart/pbl',
  'BACKUP_POS': '/kitstart/softpos',
  'ISP_POS': '/kitstart/softpos' // ISP_POS usa la stessa rotta di BACKUP_POS
}

// Rotte aggiuntive del flusso
export const FLOW_ROUTES = {
  CONTACT: '/kitstart/contact',
  CONSENTS: '/kitstart/consents',
  SUMMARY: '/kitstart/summary',
  OTP: '/kitstart/otp',
  SUBMIT: '/kitstart/submit',
  OUTCOME: '/kitstart/outcome'
}

// Ordine di priorità per determinare il primo servizio da visitare
export const SERVICE_PRIORITY = ['MICROPAYMENTS', 'PAYMAIL', 'BACKUP_POS', 'ISP_POS']

// Ordine inverso per determinare l'ultimo servizio visitato (per navigazione all'indietro)
export const SERVICE_PRIORITY_REVERSE = ['ISP_POS', 'BACKUP_POS', 'PAYMAIL', 'MICROPAYMENTS']

// Normalizza il tipo di servizio: ISP_POS e BACKUP_POS sono trattati come SoftPOS
export const normalizeServiceType = (serviceType) => {
  if (serviceType === 'ISP_POS') return 'BACKUP_POS'
  return serviceType
}

// Verifica se un servizio è di tipo SoftPOS (BACKUP_POS o ISP_POS)
export const isSoftPosService = (serviceType) => serviceType === 'BACKUP_POS' || serviceType === 'ISP_POS'

// Verifica se dobbiamo mostrare la pagina consensi
// ECCEZIONE: Se viene selezionato SOLO ISP_POS, non mostrare i consensi
export const shouldShowConsentsPage = (selectedServices = []) => {
  // Se c'è esattamente un servizio selezionato ed è ISP_POS, non mostrare i consensi
  if (selectedServices.length === 1 && selectedServices[0] === 'ISP_POS') {
    return false
  }
  // In tutti gli altri casi (incluso ISP_POS + altri servizi), mostrare i consensi
  return true
}

// Verifica se dobbiamo mostrare i consensi SoftPOS
export const shouldShowSoftPosConsents = (selectedServices = []) =>
  // ISP_POS non richiede consensi per il regolamento
  // Se c'è BACKUP_POS, mostra sempre i consensi SoftPOS (anche se c'è anche ISP_POS)
  selectedServices.includes('BACKUP_POS')


/**
 * Determina lo status di un servizio basato sui nuovi criteri T0/T1/T2
 * T0: Servizio disponibile per attivazione (SEMPRE mostrato)
 * T1: Servizio in fase di attivazione (mostrato in lista attivabili, NON in già attivati)
 * T2: Servizio completamente attivo (mostrato SOLO in "Servizi già attivati")
 * @param {Object} service - Oggetto servizio dalla response
 * @returns {Object} - { isActive, canActivate, isFullyActive, status, showOnKitStart }
 */
export const getServiceStatus = (service) => {
  if (!service) {
    return {
      isActive: false,
      canActivate: false,
      isFullyActive: false,
      status: 'NOT_AVAILABLE',
      showOnKitStart: false
    }
  }

  const status = service.additionalData?.status
  const showToActiveOnKitStart = service.additionalData?.showToActiveOnKitStart

  switch (status) {
    case 'T0':
      // In target, ma non attivo - può essere attivato (SEMPRE MOSTRATO)
      return {
        isActive: false,
        canActivate: true, // Sempre attivabile
        isFullyActive: false,
        status: 'T0',
        showOnKitStart: true // SEMPRE mostrato
      }

    case 'T1':
      // Servizio in fase di attivazione - mostrare in lista attivabili ma NON in già attivati
      return {
        isActive: true,
        canActivate: false, // Può essere modificato/esteso
        isFullyActive: false,
        status: 'T1',
        showOnKitStart: false // Mostra in lista "servizi attivabili"
      }

    case 'T2':
      // Tutti i punti vendita attivi - servizio completamente attivo
      return {
        isActive: true,
        canActivate: false, // Non può più essere modificato
        isFullyActive: true,
        status: 'T2',
        showOnKitStart: false // Non deve essere mostrato nel KitStart
      }

    default:
      // Status non riconosciuto o servizio non in target
      return {
        isActive: false,
        canActivate: false,
        isFullyActive: false,
        status: 'UNKNOWN',
        showOnKitStart: false
      }
  }
}

/**
 * Filtra i servizi che devono essere mostrati nel KitStart per l'attivazione
 * T0 (sempre mostrati) e T1 (sempre mostrati)
 * @param {Array} featureServices - Array dei servizi dalla response
 * @param {Array} supportedServices - Array dei servizi supportati
 * @returns {Array} - Array dei servizi da mostrare per attivazione
 */
export const getAvailableServices = (featureServices = [], supportedServices = ['MICROPAYMENTS', 'PAYMAIL', 'BACKUP_POS', 'ISP_POS']) => featureServices.filter(service => {
  const normalized = normalizeServiceType(service.featureType)
  if (!supportedServices.includes(normalized)) {
    return false
  }

  const serviceStatus = getServiceStatus(service)
  return serviceStatus.showOnKitStart && serviceStatus.canActivate
})

/**
 * Filtra i servizi completamente attivati da mostrare nella sezione "Servizi già attivati"
 * Solo servizi T2
 * @param {Array} featureServices - Array dei servizi dalla response
 * @param {Array} supportedServices - Array dei servizi supportati
 * @returns {Array} - Array dei servizi completamente attivati (T2)
 */
export const getActivatedServices = (featureServices = [], supportedServices = ['MICROPAYMENTS', 'PAYMAIL', 'BACKUP_POS', 'ISP_POS']) => featureServices.filter(service => {
  const normalized = normalizeServiceType(service.featureType)
  if (!supportedServices.includes(normalized)) {
    return false
  }

  const serviceStatus = getServiceStatus(service)
  return serviceStatus.isActive
})

/**
 * Filtra i servizi completamente attivati (T2)
 * @param {Array} featureServices - Array dei servizi dalla response
 * @param {Array} supportedServices - Array dei servizi supportati
 * @returns {Array} - Array dei servizi completamente attivati
 */
export const getFullyActivatedServices = (featureServices = [], supportedServices = ['MICROPAYMENTS', 'PAYMAIL', 'BACKUP_POS', 'ISP_POS']) => featureServices.filter(service => {
  const normalized = normalizeServiceType(service.featureType)
  if (!supportedServices.includes(normalized)) {
    return false
  }

  const serviceStatus = getServiceStatus(service)
  return serviceStatus.isFullyActive
})

/**
 * Determina il prossimo step nel flusso KitStart con nuova logica a 4 step fissi
 * Step 1: Selezione -> Step 2: Compilazione -> Step 3: Consensi -> Step 4: Riepilogo/OTP
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi già completati
 * @param {boolean} consentsCompleted - Se i consensi sono stati completati
 * @param {boolean} summaryCompleted - Se il summary è stato completato
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @returns {Object} - { route, isLast, step, service, totalSteps }
 */
export const getNextStep = (selectedServices = [], completedServices = [], consentsCompleted = false, summaryCompleted = false, contactCompleted = false) => {
  const totalSteps = 4

  // Verifica se tutti i servizi sono completati (incluso contact se necessario)
  const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))
  const allServicesCompleted = completedServices.length === selectedServices.length &&
    (!hasSoftPosService || contactCompleted)

  // Verifica se dobbiamo mostrare i consensi per questo set di servizi
  const shouldShowConsents = shouldShowConsentsPage(selectedServices)

  // Step 2 -> Step 3: Vai ai consensi quando tutti i servizi sono completati (solo se necessario)
  if (allServicesCompleted && shouldShowConsents && !consentsCompleted) {
    return {
      route: FLOW_ROUTES.CONSENTS,
      isLast: false,
      step: 3,
      totalSteps,
      service: 'CONSENTS'
    }
  }

  // Step 2/3 -> Step 4: Vai al summary quando i consensi sono completati (o saltati)
  if (allServicesCompleted && (consentsCompleted || !shouldShowConsents) && !summaryCompleted) {
    return {
      route: FLOW_ROUTES.SUMMARY,
      isLast: false,
      step: 4,
      totalSteps,
      service: 'SUMMARY'
    }
  }

  // Step 4: Se summary è completato, vai all'OTP (ancora dentro lo step 4)
  if (allServicesCompleted && (consentsCompleted || !shouldShowConsents) && summaryCompleted) {
    return {
      route: FLOW_ROUTES.OTP,
      isLast: true, // Questo è l'ultimo step del flusso
      step: 4,
      totalSteps,
      service: 'OTP'
    }
  }

  // Se c'è un servizio SoftPOS completato ma manca il contact
  if (hasSoftPosService && completedServices.length === selectedServices.length && !contactCompleted) {
    return {
      route: FLOW_ROUTES.CONTACT,
      isLast: false,
      step: 2, // Contact fa parte dello step 2 (compilazione)
      totalSteps,
      service: 'CONTACT'
    }
  }

  // Step 1 -> Step 2: Determina il prossimo servizio da completare
  const remainingServices = selectedServices.filter(service =>
    !completedServices.includes(service)
  )

  if (remainingServices.length > 0) {
    // Determina il prossimo servizio basato sulla priorità
    const nextService = SERVICE_PRIORITY.find(service =>
      remainingServices.includes(service)
    )

    return {
      route: SERVICE_ROUTES[nextService],
      service: nextService,
      isLast: false,
      step: 2, // Tutti i servizi sono nello step 2
      totalSteps
    }
  }

  // Fallback: se non c'è un prossimo step chiaro, resta nella pagina corrente
  return {
    route: '/',
    service: 'SELECTION',
    isLast: false,
    step: 1,
    totalSteps
  }
}

/**
 * Determina il primo step del flusso basato sui servizi selezionati
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @returns {Object} - { route, service }
 */
export const getFirstStep = (selectedServices = []) => {
  if (selectedServices.length === 0) {
    throw new Error('Nessun servizio selezionato')
  }

  // Determina il primo servizio basato sulla priorità
  const firstService = SERVICE_PRIORITY.find(service =>
    selectedServices.includes(service)
  )

  return {
    route: SERVICE_ROUTES[firstService],
    service: firstService
  }
}

/**
 * Determina l'ultimo servizio visitato nel flusso (per navigazione all'indietro)
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @returns {string} - Il tipo di servizio visitato per ultimo
 */
export const getLastVisitedService = (selectedServices = []) => {
  if (selectedServices.length === 0) {
    return null
  }

  // Determina l'ultimo servizio visitato basato sull'ordine inverso di priorità
  const lastService = SERVICE_PRIORITY_REVERSE.find(service =>
    selectedServices.includes(service)
  )

  return lastService
}

/**
 * Calcola i ministep per lo step 2 (Compilazione servizi)
 * Ogni servizio selezionato rappresenta un ministep, incluso Contact se necessario
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @param {string} currentRoute - Rotta corrente per determinare il ministep attuale
 * @returns {Object} - { currentMiniStep, totalMiniSteps }
 */
export const getMiniStepsForStep2 = (selectedServices = [], completedServices = [], contactCompleted = false, currentRoute = '') => {
  if (selectedServices.length === 0) {
    return { currentMiniStep: 1, totalMiniSteps: 1 }
  }

  // Calcola i ministep totali basati sui servizi selezionati
  let totalMiniSteps = selectedServices.length
  
  // Se c'è un servizio SoftPOS, aggiungi Contact come ministep aggiuntivo
  const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))
  if (hasSoftPosService) {
    totalMiniSteps += 1 // +1 per Contact
  }

  // Determina il ministep corrente basato sulla rotta e sui servizi completati
  let currentMiniStep = 1

  // Se siamo in una rotta di servizio specifico
  if (currentRoute) {
    // Trova l'indice del servizio corrente nell'ordine di priorità
    const currentServiceType = Object.keys(SERVICE_ROUTES).find(key => SERVICE_ROUTES[key] === currentRoute)
    
    if (currentServiceType) {
      // Conta quanti servizi prima di quello corrente sono stati selezionati
      const servicesBeforeCurrent = SERVICE_PRIORITY.filter((service, index) => {
        const currentIndex = SERVICE_PRIORITY.indexOf(currentServiceType)
        return index < currentIndex && selectedServices.includes(service)
      })
      
      currentMiniStep = servicesBeforeCurrent.length + 1
    } else if (currentRoute === FLOW_ROUTES.CONTACT) {
      // Se siamo nella pagina Contact, siamo all'ultimo ministep dello step 2
      currentMiniStep = totalMiniSteps
    }
  } else {
    // Se non abbiamo una rotta specifica, calcola basandosi sui servizi completati
    currentMiniStep = completedServices.length + 1
    
    // Se tutti i servizi sono completati e c'è Contact, vai al ministep di Contact
    if (completedServices.length === selectedServices.length && hasSoftPosService && !contactCompleted) {
      currentMiniStep = totalMiniSteps
    }
  }

  // Assicurati che currentMiniStep non superi totalMiniSteps
  currentMiniStep = Math.min(currentMiniStep, totalMiniSteps)

  return { currentMiniStep, totalMiniSteps }
}

/**
 * Determina se una rotta appartiene allo step 2 (Compilazione servizi)
 * @param {string} route - Rotta da verificare
 * @returns {boolean} - True se la rotta appartiene allo step 2
 */
export const isStep2Route = (route) => {
  const step2Routes = [
    ...Object.values(SERVICE_ROUTES), // Tutte le rotte dei servizi
    FLOW_ROUTES.CONTACT // La pagina contact
  ]
  
  return step2Routes.includes(route)
}

/**
 * Calcola il progresso completo del flusso KitStart includendo i ministep dello step 2
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati
 * @param {boolean} consentsCompleted - Se i consensi sono completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @param {boolean} summaryCompleted - Se il summary è stato completato
 * @param {boolean} otpCompleted - Se l'OTP è stato completato
 * @param {string} currentRoute - Rotta corrente per calcolare i ministep
 * @returns {Object} - { currentStep, totalSteps, percentage, currentMiniStep, totalMiniSteps }
 */
export const getProgress = (selectedServices = [], completedServices = [], consentsCompleted = false, contactCompleted = false, summaryCompleted = false, otpCompleted = false, currentRoute = '') => {
  // Nuova logica: 4 step fissi
  const totalSteps = 4
  let currentStep = 1
  
  // IMPORTANTE: Se siamo sulla pagina di selezione (/kitstart), resettiamo completamente il flusso
  // È come se l'utente stesse ricominciando da capo, può cambiare la selezione dei servizi
  if (currentRoute === '/kitstart' || currentRoute.endsWith('/kitstart')) {
    currentStep = 1
    // Reset completo: ignoriamo tutti i parametri di completamento
    // L'utente è sulla pagina di selezione, il flusso riparte da zero
  } else {
    // Logica normale per le altre pagine
    const shouldShowConsents = shouldShowConsentsPage(selectedServices)
    
    if (otpCompleted) {
      // Step 4 completato (fine flusso)
      currentStep = 4
    } else if (summaryCompleted) {
      // Siamo in Step 4 (Riepilogo + OTP)
      currentStep = 4
    } else {
      // Verifica se tutti i servizi sono stati completati (incluso contact se necessario)
      const allServicesCompleted = completedServices.length === selectedServices.length
      const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))
      const contactRequired = hasSoftPosService && !contactCompleted

      if (allServicesCompleted && !contactRequired) {
        // Step 2 completato (Compilazione servizi), andiamo al prossimo step
        // Se ci sono consensi da mostrare, vai allo step 3, altrimenti allo step 4
        if (shouldShowConsents) {
          currentStep = consentsCompleted ? 4 : 3
        } else {
          // Consensi saltati: se tutti i servizi sono completati, vai allo step 4
          currentStep = 4
        }
      } else if (selectedServices.length > 0) {
        // Servizi selezionati, siamo in Step 2 (Compilazione)
        currentStep = 2
      } else {
        // Siamo ancora in Step 1 (Selezione)
        currentStep = 1
      }
    }
  }

  // Calcola i ministep solo per lo step 2 e solo se NON siamo sulla pagina di selezione
  let currentMiniStep = 1
  let totalMiniSteps = 1
  
  if (currentStep === 2 && isStep2Route(currentRoute) && !currentRoute.endsWith('/kitstart')) {
    const miniSteps = getMiniStepsForStep2(selectedServices, completedServices, contactCompleted, currentRoute)
    currentMiniStep = miniSteps.currentMiniStep
    totalMiniSteps = miniSteps.totalMiniSteps
  }

  // Calcola la percentuale
  const percentage = totalSteps > 1 ? ((currentStep - 1) / (totalSteps - 1)) * 100 : 0

  return {
    currentStep,
    totalSteps,
    percentage: Math.round(percentage),
    currentMiniStep,
    totalMiniSteps
  }
}

/**
 * Verifica se un servizio specifico è disponibile
 * @param {string} serviceType - Tipo di servizio da verificare
 * @returns {boolean}
 */
export const isServiceAvailable = (serviceType) => Object.keys(SERVICE_ROUTES).includes(serviceType)

/**
 * Ottiene la rotta per un servizio specifico
 * @param {string} serviceType - Tipo di servizio
 * @returns {string|null} - Rotta del servizio o null se non trovata
 */
export const getServiceRoute = (serviceType) => SERVICE_ROUTES[serviceType] || null

/**
 * Determina se l'utente deve passare dalla pagina contact
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @returns {boolean} - True se deve passare dalla pagina contact
 */
export const shouldGoToContact = (selectedServices = []) => selectedServices.some(service => isSoftPosService(service))

/**
 * Determina la rotta precedente per la navigazione indietro
 * @param {string} currentRoute - Rotta corrente
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati
 * @param {boolean} consentsCompleted - Se i consensi sono completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @returns {string} - Rotta precedente
 */
export const getPreviousRoute = (currentRoute, selectedServices = [], completedServices = [], consentsCompleted = false, contactCompleted = false) => {
  const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))
  const shouldShowConsents = shouldShowConsentsPage(selectedServices)

  switch (currentRoute) {
    case '/kitstart/summary':
      // Dal riepilogo: se i consensi sono saltati, torna all'ultimo servizio/contact
      if (!shouldShowConsents) {
        if (hasSoftPosService) {
          return '/kitstart/contact'
        } else {
          // Torna all'ultimo servizio completato
          const lastService = getLastVisitedService(selectedServices)
          return SERVICE_ROUTES[lastService] || '/kitstart'
        }
      }
      // Altrimenti torna ai consensi
      return '/kitstart/consents'

    case '/kitstart/consents':
      // Dai consensi si torna sempre alla pagina contact (se SoftPOS presente) o all'ultimo servizio
      if (hasSoftPosService) {
        return '/kitstart/contact'
      } else {
        // Torna all'ultimo servizio completato
        const lastService = getLastVisitedService(selectedServices)
        return SERVICE_ROUTES[lastService] || '/kitstart'
      }

    case '/kitstart/contact':
      // Dal contact si torna sempre alla pagina softpos (selezione PV)
      return '/kitstart/softpos'

    case '/kitstart/softpos':
    case '/kitstart/pbl':
    case '/kitstart/store': {
      // Dai servizi individuali, determina il servizio precedente o torna alla selezione
      const currentServiceType = Object.keys(SERVICE_ROUTES).find(key => SERVICE_ROUTES[key] === currentRoute)
      const currentIndex = SERVICE_PRIORITY.indexOf(currentServiceType)

      if (currentIndex > 0) {
        // Trova il servizi precedente che è stato selezionato
        for (let i = currentIndex - 1; i >= 0; i--) {
          const prevService = SERVICE_PRIORITY[i]
          if (selectedServices.includes(prevService)) {
            return SERVICE_ROUTES[prevService]
          }
        }
      }

      // Se non c'è un servizio precedente, torna alla selezione
      return '/kitstart'
    }

    case '/kitstart/otp':
      // Dall'OTP si torna sempre al summary
      return '/kitstart/summary'

    default:
      // Fallback: torna alla pagina di selezione
      return '/kitstart'
  }
}

/**
 * Ottiene la descrizione dei 4 step del nuovo flusso KitStart
 * @param {number} stepNumber - Numero dello step (1-4)
 * @returns {Object} - { title, description, routes }
 */
export const getStepInfo = (stepNumber) => {
  const steps = {
    1: {
      title: 'Selezione servizi',
      description: 'Seleziona i servizi che vuoi attivare',
      routes: ['/']
    },
    2: {
      title: 'Compilazione servizi',
      description: 'Completa la configurazione per ogni servizio selezionato',
      routes: ['/store', '/pbl', '/softpos', '/contact']
    },
    3: {
      title: 'Consensi',
      description: 'Accetta i consensi necessari per l\'attivazione dei servizi',
      routes: ['/consents']
    },
    4: {
      title: 'Riepilogo e OTP',
      description: 'Verifica i dati e completa l\'attivazione con OTP obbligatorio',
      routes: ['/summary', '/otp', '/submit', '/outcome']
    }
  }

  return steps[stepNumber] || null
}

/**
 * Determina la rotta successiva corretta per la navigazione avanti
 * Gestisce correttamente il flusso SoftPOS -> Contact per evitare skip
 * @param {string} currentRoute - Rotta corrente
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati
 * @param {boolean} consentsCompleted - Se i consensi sono completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @param {boolean} summaryCompleted - Se il summary è stato completato
 * @returns {string} - Rotta successiva
 */
export const getNextRoute = (currentRoute, selectedServices = [], completedServices = [], consentsCompleted = false, contactCompleted = false, summaryCompleted = false) => {
  const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))

  // Gestione specifica per SoftPOS: se ho completato softpos ma non contact, devo andare a contact
  if (currentRoute === '/kitstart/softpos' && hasSoftPosService && !contactCompleted) {
    return '/kitstart/contact'
  }

  // Se sono in contact e l'ho completato, uso la logica normale di getNextStep
  if (currentRoute === '/kitstart/contact' && contactCompleted) {
    const nextStep = getNextStep(selectedServices, completedServices, consentsCompleted, summaryCompleted, contactCompleted)
    return nextStep.route
  }

  // Per tutte le altre rotte, uso la logica standard di getNextStep
  const nextStep = getNextStep(selectedServices, completedServices, consentsCompleted, summaryCompleted, contactCompleted)
  return nextStep.route
}

/**
 * Genera il payload per il submit finale
 * @param {Object} selections - Oggetto con le selezioni per ogni servizio
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Object} contactData - Dati di contatto (email, phone)
 * @param {Object} consents - Consensi dell'utente
 * @param {Object} scaData - Dati SCA (otp, transactionId, etc.)
 * @returns {Object} - Payload per la chiamata di submit
 */
export const generateSubmitPayload = (selections = {}, selectedServices = [], contactData = {}, consents = {}, scaData = {}, featureServices = []) => {
  const payload = {
    featuresToActive: selectedServices.map(service => {
      // Mappa i nomi dei servizi alla nomenclatura del backend
      switch (service) {
        case 'PAYMAIL': return 'PAYMAIL'
        case 'BACKUP_POS': return 'BACKUP_POS'
        case 'ISP_POS': return 'ISP_POS' // Mantieni il tipo originale per il backend
        default: return service
      }
    })
  }

  // Aggiunge informazioni sugli status dei servizi per debug (opzionale)
  const serviceStatusMap = {}
  featureServices.forEach(service => {
    if (selectedServices.includes(service.featureType)) {
      serviceStatusMap[service.featureType] = getServiceStatus(service)
    }
  })

  // Micropayments: solo consenso regolamentare
  if (selectedServices.includes('MICROPAYMENTS')) {
    payload.micropayment = {
      consentRegulation: consents.micropayments_nexi || true
    }
  }

  // PayMail (PAYMAIL): dati del negozio selezionato
  if (selectedServices.includes('PAYMAIL') && selections.paymail) {
    const paymailData = selections.paymail

    // Se paymail ha la struttura {shop, selectedBank}
    if (paymailData.shop && paymailData.selectedBank) {
      payload.payMail = {
        abi: paymailData.selectedBank.terminalCode || paymailData.selectedBank.id,
        phone: contactData.phone || paymailData.shop.phone,
        email: contactData.email || paymailData.shop.email,
        shopCode: paymailData.shop.code,
        shopId: paymailData.shop.id,
        bankName: paymailData.selectedBank.infoLabel
      }
    }
    // Se paymail è un array di selezioni (fallback)
    else if (Array.isArray(paymailData) && paymailData.length > 0) {
      const selectedPaymailShop = paymailData[0]
      payload.payMail = {
        abi: selectedPaymailShop.abiCode || selectedPaymailShop.abi,
        phone: contactData.phone || selectedPaymailShop.phone,
        email: contactData.email || selectedPaymailShop.email,
        shopCode: selectedPaymailShop.shopCode || selectedPaymailShop.codicePuntoVendita,
        shopId: selectedPaymailShop.shopId || selectedPaymailShop.id,
        bankName: selectedPaymailShop.bankName || selectedPaymailShop.denominazioneBanca
      }
    }
  }

  // BACKUP_POS: struttura complessa con companies
  if (selectedServices.includes('BACKUP_POS') && selections.softpos) {
    const softposData = selections.softpos

    // Se softpos ha la struttura {shop, selectedBank}
    if (softposData.shop && softposData.selectedBank) {
      payload.backupPos = {
        email: contactData.email || softposData.shop.email,
        companies: [{
          companyCode: nullSafe(() => softposData.shop.companyCode, ''),
          companyName: nullSafe(() => softposData.shop.companyName, ''),
          shops: [{
            bankName: nullSafe(() => softposData.selectedBank.originalBank.bankName, ''),
            abiCode: nullSafe(() => softposData.selectedBank.originalBank.abiCode, ''),
            address: nullSafe(() => softposData.shop.address, ''),
            shopName: nullSafe(() => softposData.shop.name, ''),
            shopCode: nullSafe(() => softposData.shop.code, '')
          }]
        }],
        deviceId: softposData.shop.deviceId || 2,
        otp: scaData.otp || '',
        midTransactionId: scaData.transactionId || scaData.midTransactionId || ''
      }
    }
    // Se softpos è un array di selezioni (fallback)
    else if (Array.isArray(softposData) && softposData.length > 0) {
      const selectedSoftposShop = softposData[0]
      payload.backupPos = {
        email: contactData.email || selectedSoftposShop.email,
        companies: [{
          companyCode: selectedSoftposShop.companyCode || selectedSoftposShop.codiceAzienda,
          companyName: selectedSoftposShop.companyName || selectedSoftposShop.denominazioneAzienda,
          shops: [{
            bankName: selectedSoftposShop.bankName || selectedSoftposShop.denominazioneBanca,
            abiCode: selectedSoftposShop.abiCode || selectedSoftposShop.abi,
            address: selectedSoftposShop.address || selectedSoftposShop.indirizzo,
            shopName: selectedSoftposShop.shopName || selectedSoftposShop.denominazione,
            shopCode: selectedSoftposShop.shopCode || selectedSoftposShop.codicePuntoVendita
          }]
        }],
        deviceId: selectedSoftposShop.deviceId || 2,
        otp: scaData.otp || '',
        midTransactionId: scaData.transactionId || scaData.midTransactionId || ''
      }
    }
  }

  // ISP_POS: email MEPS + dati aggiuntivi
  if (selectedServices.includes('ISP_POS') && selections.softpos) {
    const softposData = selections.softpos

    // Se softpos ha la struttura {shop, selectedBank}
    if (softposData.shop && softposData.selectedBank) {
      payload.ispPos = {
        mepsMail: contactData.email || softposData.mepsEmail || softposData.shop.mepsEmail || softposData.shop.email || "",
        terminalId: softposData.selectedBank.terminalCode || softposData.selectedBank.id || softposData.terminalId || "",
        shopDocId: softposData.shop.code || softposData.shop.shopCode || softposData.shopDocId || "",
        shopName: softposData.shop.name || softposData.shop.shopName || softposData.shopName || ""
      }
    }
    // Se softpos è un array di selezioni (fallback)
    else if (Array.isArray(softposData) && softposData.length > 0) {
      const selectedSoftposShop = softposData[0]
      payload.ispPos = {
        mepsMail: contactData.email || selectedSoftposShop.mepsEmail || selectedSoftposShop.email || "",
        terminalId: selectedSoftposShop.terminalId || selectedSoftposShop.abiCode || selectedSoftposShop.abi || selectedSoftposShop.terminalCode || "",
        shopDocId: selectedSoftposShop.shopDocId || selectedSoftposShop.shopCode || selectedSoftposShop.codicePuntoVendita || selectedSoftposShop.code || "",
        shopName: selectedSoftposShop.shopName || selectedSoftposShop.denominazione || selectedSoftposShop.name || ""
      }
    }
  }

  return payload
}

/**
 * Verifica se la pagina contact deve essere considerata nel flusso corrente
 * Importante per evitare che venga skippata dopo la navigazione indietro
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @returns {boolean} - True se contact deve essere inclusa nel flusso
 */
export const shouldIncludeContactInFlow = (selectedServices = [], completedServices = [], contactCompleted = false) => {
  const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))

  if (!hasSoftPosService) {
    return false // Non c'è SoftPOS, contact non serve
  }

  // Se SoftPOS è nei servizi selezionati ma non completato, contact non deve essere ancora mostrato
  const softPosCompleted = selectedServices.some(service =>
    isSoftPosService(service) && completedServices.includes(service)
  )

  // Contact deve essere mostrato solo dopo che SoftPOS è completato
  return softPosCompleted && !contactCompleted
}

/**
 * Verifica se il flusso KitStart può proseguire con i dati disponibili
 * Usato per controllare se l'utente ha perso i dati dopo un refresh
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Object} selections - Oggetto con le selezioni fatte
 * @returns {boolean} - True se il flusso può proseguire
 */
export const canContinueFlow = (selectedServices = [], selections = {}, featureServices = []) => {
  // Se non ci sono servizi selezionati, non può proseguire
  if (!selectedServices || selectedServices.length === 0) {
    return false
  }

  // Verifica che tutti i servizi selezionati siano ancora disponibili
  const availableServices = getAvailableServices(featureServices)
  const availableServiceTypes = availableServices.map(s => s.featureType)

  for (const service of selectedServices) {
    // Il servizio deve essere ancora disponibile per l'attivazione
    if (!availableServiceTypes.includes(service)) {
      return false
    }

    // Verifica che il servizio abbia almeno una selezione
    let hasValidSelection = false

    switch (service) {
      case 'MICROPAYMENTS':
        hasValidSelection = selections.selectedMicropaymentsShops &&
          selections.selectedMicropaymentsShops.length > 0
        break
      case 'PAYMAIL':
        hasValidSelection = selections.selectedPaymailShops &&
          selections.selectedPaymailShops.length > 0
        break
      case 'BACKUP_POS':
      case 'ISP_POS':
        hasValidSelection = selections.selectedSoftposShops &&
          selections.selectedSoftposShops.length > 0
        break
      default:
        hasValidSelection = false
    }

    if (!hasValidSelection) {
      return false
    }
  }

  return true
}

/**
 * Determina se bisogna andare ai consensi dopo aver completato un servizio
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati (dopo l'aggiunta del servizio appena completato)
 * @param {boolean} consentsCompleted - Se i consensi sono già stati completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata (solo per BACKUP_POS)
 * @returns {boolean} - True se bisogna andare ai consensi
 */
export const shouldGoToConsents = (selectedServices = [], completedServices = [], consentsCompleted = false, contactCompleted = false) => {
  // Se i consensi sono già stati completati, non ci andare di nuovo
  if (consentsCompleted) {
    return false
  }

  // Se tutti i servizi selezionati sono stati completati
  const allServicesCompleted = completedServices.length === selectedServices.length

  if (!allServicesCompleted) {
    return false
  }

  // Se c'è un servizio SoftPOS nei servizi selezionati, bisogna completare anche la pagina contact
  const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))
  if (hasSoftPosService) {
    return contactCompleted
  }

  // Altrimenti, se tutti i servizi sono completati, vai ai consensi
  return true
}

/**
 * Determina la rotta successiva dopo aver completato un servizio specifico
 * @param {string} completedService - Il servizio appena completato
 * @param {Array} selectedServices - Array dei servizi selezionati
 * @param {Array} completedServices - Array dei servizi completati (prima dell'aggiunta del servizio appena completato)
 * @param {boolean} consentsCompleted - Se i consensi sono stati completati
 * @param {boolean} contactCompleted - Se la pagina contact è stata completata
 * @returns {Object} - { route, service, shouldGoToConsents, shouldGoToContact }
 */
export const getNextRouteAfterService = (completedService, selectedServices = [], completedServices = [], consentsCompleted = false, contactCompleted = false) => {
  // Aggiungi il servizio appena completato alla lista
  const newCompletedServices = [...completedServices, completedService]

  // Verifica se questo era l'ultimo servizio
  const allServicesCompleted = newCompletedServices.length === selectedServices.length

  // Se l'utente ha completato un servizio SoftPOS e deve ancora fare la pagina contact
  if (isSoftPosService(completedService) && !contactCompleted) {
    return {
      route: FLOW_ROUTES.CONTACT,
      service: 'CONTACT',
      shouldGoToConsents: false,
      shouldGoToContact: true
    }
  }

  if (allServicesCompleted && !consentsCompleted) {
    // Se tutti i servizi sono completati ma c'è un servizio SoftPOS e contact non è completato
    const hasSoftPosService = selectedServices.some(service => isSoftPosService(service))
    if (hasSoftPosService && !contactCompleted) {
      return {
        route: FLOW_ROUTES.CONTACT,
        service: 'CONTACT',
        shouldGoToConsents: false,
        shouldGoToContact: true
      }
    }

    // Se tutti i servizi sono completati e contact è completato (o non necessario)
    // Vai sempre ai consensi per tutti i servizi
    return {
      route: FLOW_ROUTES.CONSENTS,
      service: 'CONSENTS',
      shouldGoToConsents: true,
      shouldGoToContact: false
    }
  } else if (allServicesCompleted && consentsCompleted) {
    // Se tutto è completato, vai al summary
    return {
      route: FLOW_ROUTES.SUMMARY,
      service: 'SUMMARY',
      shouldGoToConsents: false,
      shouldGoToContact: false
    }
  } else {
    // Altrimenti, vai al prossimo servizio
    const nextStep = getNextStep(selectedServices, newCompletedServices, consentsCompleted, false, contactCompleted)
    return {
      route: nextStep.route,
      service: nextStep.service,
      shouldGoToConsents: false,
      shouldGoToContact: false
    }
  }
}
